import { Close as CloseIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  Link as MuiLink,
  Typography,
} from "@mui/material";
import React from "react";
import { Link } from "react-router-dom";

import { monospaceStyle, panelShadow } from "../../../theme";
import { TreemapNode } from "../../../types/analytics";
import { EntityClickHandler } from "../../../types/entityRegistry";
import { AnalyticsSelection, exploreAddress } from "../analyticsState";
import { OWN_LABEL, TreemapIndex, ownCount } from "./treemapLayout";
import {
  classLabel,
  classSelection,
  counted,
  isBranchOther,
  isRootOther,
} from "./treemapModel";

export const OWN_PREFIX = "own:";

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
    <Typography
      component="span"
      color="text.secondary"
      sx={{ flex: "0 0 92px", fontSize: 12, lineHeight: 1.5 }}
    >
      {label}
    </Typography>
    <Typography component="div" sx={{ flex: 1, minWidth: 0, fontSize: 12.5, lineHeight: 1.5 }}>
      {children}
    </Typography>
  </Box>
);

// The ways on, as quiet links in a row
const ACTION_SX = {
  p: 0,
  minWidth: 0,
  fontSize: 12.5,
  fontWeight: 500,
  lineHeight: 1.5,
  textTransform: "none",
  color: "link.main",
  "&:hover": { backgroundColor: "transparent", textDecoration: "underline" },
} as const;

// A class beneath: its name, its count at the far end
const BENEATH_ROW_SX = {
  display: "flex",
  alignItems: "baseline",
  justifyContent: "space-between",
  gap: 1,
} as const;
const BENEATH_COUNT_SX = {
  ...monospaceStyle,
  flexShrink: 0,
  fontSize: 11,
  lineHeight: 1.5,
} as const;

const percent = (share: number): string => `${Math.round(share * 100)}%`;

// What a class is, in the words of its rank and kind
const kindOf = (node: TreemapNode): string => {
  if (node.gap) {
    return "class · nothing on record";
  }
  if (isRootOther(node)) {
    return "filed under no class";
  }
  if (isBranchOther(node)) {
    return "filed on the class, under none of its classes";
  }
  return node.rank === null ? "class" : `${node.rank} class`;
};

interface TreemapPanelProps {
  index: TreemapIndex;
  // A class id, or `own:<class id>` for the products filed on a class
  selectedKey: string;
  // The class the treemap is drilled into; null at the top
  rootId: string | null;
  // Whether the classes with nothing on record are drawn
  gaps: boolean;
  selection: AnalyticsSelection;
  onSelect: (key: string) => void;
  onClear: () => void;
  onDrill: (nodeId: string) => void;
  onOpenEntity: EntityClickHandler;
}

// The panel of a selected class, raised over the corner of the canvas: its
// counts with the Explore link where a filter reproduces them, what lies
// beneath it and the ways on
const TreemapPanel: React.FC<TreemapPanelProps> = ({
  index,
  selectedKey,
  rootId,
  gaps,
  selection,
  onSelect,
  onClear,
  onDrill,
  onOpenEntity,
}) => {
  const own = selectedKey.startsWith(OWN_PREFIX);
  const node = index.byId.get(own ? selectedKey.slice(OWN_PREFIX.length) : selectedKey);
  if (!node) {
    return null;
  }
  const kids = index.children.get(node.id) ?? [];
  // A way on only where the drill draws something new: not into the class
  // the treemap already shows, nor into classes that are not drawn
  const canDrill = node.id !== rootId && kids.some((kid) => gaps || !kid.gap);
  const parent = node.parent_id === null ? null : index.byId.get(node.parent_id);

  const frame = (title: string, subtitle: string, body: React.ReactNode) => (
    <Box
      component="aside"
      aria-label="Selected class"
      sx={{
        position: "absolute",
        top: 12,
        right: 12,
        zIndex: 2,
        width: 300,
        maxWidth: "calc(100% - 24px)",
        maxHeight: "calc(100% - 24px)",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        gap: 0.75,
        p: "10px 12px 12px",
        overflow: "auto",
        border: 1,
        borderColor: "divider",
        borderRadius: (theme) => `${theme.radius.surface}px`,
        backgroundColor: "background.raised",
        boxShadow: (theme) => panelShadow(theme),
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            component="h3"
            sx={{ m: 0, fontSize: 13.5, fontWeight: 600, lineHeight: 1.4, overflowWrap: "anywhere" }}
          >
            {title}
          </Typography>
          <Typography
            component="div"
            color="text.secondary"
            sx={{ ...monospaceStyle, fontSize: 11, lineHeight: 1.5 }}
          >
            {subtitle}
          </Typography>
        </Box>
        <IconButton
          size="small"
          aria-label="Clear selection"
          onClick={onClear}
          sx={{ m: "-4px -6px 0 0", color: "text.secondary" }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
      {body}
    </Box>
  );

  if (own) {
    return frame(
      OWN_LABEL,
      `on ${classLabel(node)}`,
      <>
        <Field label="Products">{counted(ownCount(index, node), "product")}</Field>
        <Typography variant="caption" color="text.secondary">
          Filed on {classLabel(node)} itself and on none of its classes. Explore
          cannot filter for these: its class filter counts the classes beneath too.
        </Typography>
        <Box>
          <Button size="small" disableRipple onClick={() => onSelect(node.id)} sx={ACTION_SX}>
            Select {classLabel(node)}
          </Button>
        </Box>
      </>,
    );
  }

  const picked = classSelection(selection, node);
  const canOpen = !node.other;
  return frame(
    classLabel(node),
    kindOf(node),
    <>
      <Field label="Products">
        {picked ? (
          <MuiLink component={Link} to={exploreAddress(picked)} underline="hover">
            {counted(node.product_count, "product")}
          </MuiLink>
        ) : (
          counted(node.product_count, "product")
        )}
        {isBranchOther(node) && (
          <Typography variant="caption" component="div" color="text.secondary">
            filed on {parent ? classLabel(parent) : "the class"} but under none of its
            classes
          </Typography>
        )}
        {node.gap && (
          <Typography variant="caption" component="div" color="text.secondary">
            Nothing on record for this class in the scope.
          </Typography>
        )}
        {!isBranchOther(node) && !node.gap && kids.length > 0 && (
          <Typography variant="caption" component="div" color="text.secondary">
            counts every product under the class and the classes beneath it
          </Typography>
        )}
      </Field>
      <Field label="Makers">{node.maker_count}</Field>
      <Field label="Articles">{node.articles}</Field>
      {!node.gap && (
        <Field label="Single source">{percent(node.single_source_share)}</Field>
      )}
      {node.domestic_share !== null && (
        <Field label="Domestic share">{percent(node.domestic_share)}</Field>
      )}
      {node.thin && (
        <Typography variant="caption" color="text.secondary">
          Under the support floor: a hint, not a finding.
        </Typography>
      )}
      {kids.length > 0 && (
        <Box component="section" aria-label="Classes beneath">
          <Typography
            component="div"
            color="text.secondary"
            sx={{
              mb: 0.5,
              fontSize: 11,
              fontWeight: 500,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
            }}
          >
            Beneath
          </Typography>
          <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none", display: "grid", gap: 0.25 }}>
            {kids.map((kid) => (
              <Box component="li" key={kid.id} sx={BENEATH_ROW_SX}>
                <Button
                  size="small"
                  disableRipple
                  onClick={() => onSelect(kid.id)}
                  sx={{ ...ACTION_SX, textAlign: "left" }}
                >
                  {classLabel(kid)}
                </Button>
                <Typography component="span" color="text.secondary" sx={BENEATH_COUNT_SX}>
                  {kid.gap ? "nothing on record" : counted(kid.product_count, "product")}
                </Typography>
              </Box>
            ))}
            {ownCount(index, node) > 0 && (
              <Box component="li" sx={BENEATH_ROW_SX}>
                <Button
                  size="small"
                  disableRipple
                  onClick={() => onSelect(`${OWN_PREFIX}${node.id}`)}
                  sx={{ ...ACTION_SX, textAlign: "left", fontStyle: "italic" }}
                >
                  {OWN_LABEL}
                </Button>
                <Typography component="span" color="text.secondary" sx={BENEATH_COUNT_SX}>
                  {counted(ownCount(index, node), "product")}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}
      {(canDrill || canOpen) && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            flexWrap: "wrap",
            pt: 0.75,
            borderTop: 1,
            borderColor: "divider",
          }}
        >
          {canDrill && (
            <Button size="small" disableRipple onClick={() => onDrill(node.id)} sx={ACTION_SX}>
              Drill into
            </Button>
          )}
          {canDrill && canOpen && (
            <Typography component="span" color="text.disabled" aria-hidden sx={{ fontSize: 12.5 }}>
              ·
            </Typography>
          )}
          {canOpen && (
            <Button
              size="small"
              disableRipple
              onClick={() => onOpenEntity(node.category.id, node.category.type)}
              sx={ACTION_SX}
            >
              Open class
            </Button>
          )}
        </Box>
      )}
    </>,
  );
};

export default TreemapPanel;
